import { Activity, ChevronDown, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";

import { FLASHPAY_ROLES, RequireRole } from "@/core/auth";
import { Badge } from "@/core/components/ui/badge";
import { Button } from "@/core/components/ui/button";
import { Card } from "@/core/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/core/components/ui/collapsible";
import { NoTranslate } from "@/core/components/ui/no-translate";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/core/components/ui/sheet";
import useTypedTranslation, {
  type TFunctionType,
} from "@/core/hooks/use-typed-translation";
import { cn } from "@/core/utils/func/cn";
import type { ReportingGaugeDto } from "@/features/reporting/api/dto";
import { CRITICAL_GAUGE_NAMES } from "@/features/reporting/api/dto";
import { reportingEndpoints } from "@/features/reporting/api/endpoints";
import { ReportingGaugesBanner } from "@/features/reporting/components/reporting-gauges-banner";

const isAlerting = (name: string, value: number): boolean => {
  if (name === "flashpay_ledger_balance_gap_minor") return value !== 0;
  return value > 0;
};

const PROVENANCE_ENDPOINTS = [
  reportingEndpoints.wallet.gauges,
  reportingEndpoints.kyc.gauges,
  reportingEndpoints.ledger.gauges,
  reportingEndpoints.merchant.gauges,
  reportingEndpoints["fee-engine"].gauges,
] as const;

export type ReportingGaugesDrawerProps = {
  gauges: ReportingGaugeDto[];
  names?: readonly string[];
  titleKey?: TFunctionType;
  descriptionKey?: TFunctionType;
  provenanceEndpoints?: readonly string[];
};

/**
 * Card compacte (ligne du titre) + panneau latéral gauche — jauges §7.5.
 * Provenance API réservée au superadmin, masquée par défaut.
 */
export function ReportingGaugesDrawer({
  gauges,
  names = CRITICAL_GAUGE_NAMES,
  titleKey = "dashboard.gauges.title",
  descriptionKey = "dashboard.gauges.drawerDescription",
  provenanceEndpoints = PROVENANCE_ENDPOINTS,
}: ReportingGaugesDrawerProps) {
  const { t } = useTypedTranslation();
  const [open, setOpen] = useState(false);
  const [showProvenance, setShowProvenance] = useState(false);

  const alertCount = useMemo(
    () =>
      names.filter((name) => {
        const gauge = gauges.find((entry) => entry.name === name);
        return gauge ? isAlerting(gauge.name, gauge.value) : false;
      }).length,
    [gauges, names]
  );

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setShowProvenance(false);
  };

  return (
    <>
      <Card
        role="button"
        tabIndex={0}
        className="relative mt-0 shrink-0 cursor-pointer border-accent/40 hover:translate-y-0 hover:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        onClick={() => setOpen(true)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-1 rounded-t-[20px] bg-gradient-accent"
        />
        <div className="flex items-center gap-3 px-3 py-2">
          <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-gradient-accent text-brand-950">
            <Activity className="size-4" aria-hidden />
          </div>
          <div className="min-w-0 text-left">
            <p className="text-sm font-semibold leading-none text-card-foreground">
              {t(titleKey)}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {alertCount > 0
                ? t("dashboard.gauges.alertCount", { count: alertCount })
                : t("dashboard.gauges.allClear")}
            </p>
          </div>
          <span className="inline-flex items-center text-xs font-medium text-accent-dark dark:text-accent-light">
            {t("dashboard.gauges.consult")}
            <ChevronRight className="ml-0.5 size-3.5" aria-hidden />
          </span>
        </div>
      </Card>

      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent
          side="left"
          className="flex h-full w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-md"
        >
          <div aria-hidden className="h-1 shrink-0 bg-gradient-accent" />
          <SheetHeader className="space-y-0 border-b border-accent/20 px-6 py-5 pr-12 text-left">
            <div className="flex items-start gap-3">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-gradient-accent text-brand-950">
                <Activity className="size-5" aria-hidden />
              </div>
              <div className="min-w-0 space-y-1.5">
                <SheetTitle>{t(titleKey)}</SheetTitle>
                <SheetDescription>{t(descriptionKey)}</SheetDescription>
              </div>
            </div>
          </SheetHeader>

          <ScrollArea className="min-h-0 flex-1">
            <div className="flex flex-col gap-4 px-6 py-5">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-medium text-muted-foreground">
                  {t("dashboard.gauges.cardHint")}
                </p>
                <Badge variant={alertCount > 0 ? "warning" : "success"}>
                  {alertCount > 0
                    ? t("dashboard.gauges.alertCount", { count: alertCount })
                    : t("dashboard.gauges.allClear")}
                </Badge>
              </div>
              <ReportingGaugesBanner
                gauges={gauges}
                compact
                names={names}
                className="grid-cols-1 sm:grid-cols-1"
              />
            </div>
          </ScrollArea>

          <RequireRole roles={[FLASHPAY_ROLES.SUPERADMIN]}>
            <div className="border-t border-border bg-muted/30 px-6 py-4">
              <Collapsible
                open={showProvenance}
                onOpenChange={setShowProvenance}
              >
                <CollapsibleTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full justify-between border-accent/30 hover:border-accent"
                  >
                    {t("dashboard.gauges.provenance.title")}
                    <ChevronDown
                      className={cn(
                        "size-4 shrink-0 transition-transform",
                        showProvenance && "rotate-180"
                      )}
                      aria-hidden
                    />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="overflow-hidden">
                  <div className="mt-3 flex flex-col gap-3 rounded-xl border border-border bg-card p-4">
                    <p className="text-xs leading-relaxed text-muted-foreground">
                      {t("dashboard.gauges.provenance.guide")}
                    </p>
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-foreground">
                        {t("dashboard.gauges.provenance.endpoints")}
                      </p>
                      <ul className="space-y-1.5">
                        {provenanceEndpoints.map((endpoint) => (
                          <li key={endpoint}>
                            <NoTranslate
                              as="span"
                              className="block break-all font-mono text-[11px] text-muted-foreground"
                            >
                              {endpoint}
                            </NoTranslate>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t("dashboard.gauges.provenance.also")}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t("dashboard.gauges.provenance.roles")}
                    </p>
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-foreground">
                        {t("dashboard.gauges.provenance.names")}
                      </p>
                      <ul className="space-y-1.5">
                        {names.map((name) => (
                          <li key={name}>
                            <NoTranslate
                              as="span"
                              className="block break-all font-mono text-[11px] text-muted-foreground"
                            >
                              {name}
                            </NoTranslate>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>
          </RequireRole>
        </SheetContent>
      </Sheet>
    </>
  );
}
